import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";

import { useApi } from "@/lib/api";

const VALUES = [
  { id: "honesty", label: "Honesty", hint: "saying what's true" },
  { id: "creativity", label: "Creativity", hint: "making something new" },
  { id: "connection", label: "Connection", hint: "real closeness with people" },
  { id: "freedom", label: "Freedom", hint: "agency over my time + choices" },
  { id: "growth", label: "Growth", hint: "becoming more capable" },
  { id: "service", label: "Service", hint: "being useful to others" },
  { id: "playfulness", label: "Playfulness", hint: "lightness and fun" },
  { id: "courage", label: "Courage", hint: "moving toward fear" },
  { id: "stability", label: "Stability", hint: "predictability and calm" },
  { id: "craft", label: "Craft", hint: "doing things well" },
  { id: "wisdom", label: "Wisdom", hint: "seeing things clearly" },
  { id: "health", label: "Health", hint: "energy and vitality" },
];

type Step = "welcome" | "values" | "habit" | "done";

export default function OnboardingPage() {
  const api = useApi();
  const nav = useNavigate();
  const { user } = useUser();
  const [step, setStep] = useState<Step>("welcome");
  const [values, setValues] = useState<string[]>([]);
  const [anchor, setAnchor] = useState("");
  const [behavior, setBehavior] = useState("");
  const [celebration, setCelebration] = useState("");
  const [busy, setBusy] = useState(false);

  // Skip onboarding if user already has values + at least one completed practice.
  useEffect(() => {
    api<{ profile: { preferences?: Record<string, string> } }>("/api/me").then((r) => {
      const has = Boolean(r.profile?.preferences?.values);
      if (has) nav("/today", { replace: true });
    }).catch(() => {});
  }, [api, nav]);

  function toggleValue(id: string) {
    setValues((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id].slice(-3)));
  }

  async function finishValues() {
    setBusy(true);
    try {
      // Persist via user_profile preferences + record a completed values practice.
      await api("/api/me/profile-preferences", {
        method: "POST",
        body: JSON.stringify({ values: values.join(",") }),
      }).catch(() => {});
      const r = await api<{ session: { id: string } }>("/api/practice/sessions", {
        method: "POST",
        body: JSON.stringify({ template_id: "values_clarification" }),
      });
      await api(`/api/practice/sessions/${r.session.id}`, {
        method: "PATCH",
        body: JSON.stringify({ responses: { values }, completed: true }),
      });
      setStep("habit");
    } finally {
      setBusy(false);
    }
  }

  async function finishHabit() {
    setBusy(true);
    try {
      // Promote the declared habit to a first-class /me/habit record so it
      // surfaces on /today as a one-tap completion. Also log a completed
      // session for journey continuity.
      await api("/api/me/habit", {
        method: "POST",
        body: JSON.stringify({ anchor, behavior, celebration }),
      }).catch(() => {});
      const r = await api<{ session: { id: string } }>("/api/practice/sessions", {
        method: "POST",
        body: JSON.stringify({ template_id: "tiny_habit_design" }),
      });
      await api(`/api/practice/sessions/${r.session.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          responses: { anchor, behavior, celebration },
          completed: true,
        }),
      });
      setStep("done");
      setTimeout(() => nav("/today", { replace: true }), 1200);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex h-full max-w-xl items-center px-4">
      <div className="w-full">
        {step === "welcome" && (
          <div className="space-y-5 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-neutral-900 text-2xl text-white">
              🌱
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {user?.firstName ? `Hi, ${user.firstName}.` : "Hi."}
            </h1>
            <p className="mx-auto max-w-md text-base leading-relaxed text-neutral-600">
              I'm Cogi. I work in 5-minute daily rituals — grounded in ACT, Tiny Habits, and CBT.
              Let's set up your direction in two short steps.
            </p>
            <button
              onClick={() => setStep("values")}
              className="rounded-2xl bg-neutral-900 px-5 py-3 text-sm font-medium text-white"
            >
              Begin →
            </button>
          </div>
        )}

        {step === "values" && (
          <div className="space-y-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              Step 1 of 2 · ACT
            </div>
            <h2 className="text-2xl font-semibold tracking-tight">What matters to you right now?</h2>
            <p className="text-sm text-neutral-600">
              Pick three. Not forever — just for this season. These become your direction; what
              you're spending five minutes a day toward.
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {VALUES.map((v) => {
                const isPicked = values.includes(v.id);
                return (
                  <button
                    key={v.id}
                    onClick={() => toggleValue(v.id)}
                    className={
                      "rounded-2xl border p-3 text-left text-sm transition " +
                      (isPicked
                        ? "border-neutral-900 bg-neutral-900 text-white"
                        : "border-neutral-200 bg-white hover:border-neutral-400")
                    }
                  >
                    <div className="font-medium">{v.label}</div>
                    <div
                      className={
                        "text-[11px] " + (isPicked ? "text-neutral-300" : "text-neutral-500")
                      }
                    >
                      {v.hint}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end pt-2">
              <button
                disabled={values.length !== 3 || busy}
                onClick={finishValues}
                className="rounded-2xl bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-30"
              >
                {values.length === 3 ? "These three →" : `Pick 3 (${values.length}/3)`}
              </button>
            </div>
          </div>
        )}

        {step === "habit" && (
          <div className="space-y-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              Step 2 of 2 · Tiny Habits
            </div>
            <h2 className="text-2xl font-semibold tracking-tight">Design your first tiny habit</h2>
            <p className="text-sm text-neutral-600">
              The smallest unit of change. Anchor it to a routine that already happens; make the
              behavior under 30 seconds; celebrate immediately. We can change it tomorrow.
            </p>
            <Field
              label="After I…"
              sub="An existing routine that already happens daily."
              value={anchor}
              onChange={setAnchor}
              placeholder="brush my teeth"
            />
            <Field
              label="I will…"
              sub="A tiny action under 30 seconds."
              value={behavior}
              onChange={setBehavior}
              placeholder="do one push-up"
            />
            <Field
              label="Then I'll celebrate by…"
              sub="Tiny and immediate."
              value={celebration}
              onChange={setCelebration}
              placeholder="say YES out loud"
            />
            <div className="flex justify-between pt-2">
              <button
                onClick={() => setStep("values")}
                className="rounded-2xl border border-neutral-200 px-4 py-2 text-sm"
              >
                ← Back
              </button>
              <button
                disabled={!anchor.trim() || !behavior.trim() || !celebration.trim() || busy}
                onClick={finishHabit}
                className="rounded-2xl bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-30"
              >
                Lock it in →
              </button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-3 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-2xl text-white">
              ✓
            </div>
            <h2 className="text-2xl font-semibold">You're set.</h2>
            <p className="text-sm text-neutral-600">Heading to your first day…</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  sub?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-neutral-800">{props.label}</label>
      {props.sub && <p className="mb-1 text-[11px] text-neutral-500">{props.sub}</p>}
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400"
      />
    </div>
  );
}
